import type { ComponentType as ReactComponentType, ReactNode } from 'react';
import React, { useContext } from 'react';
import name from 'react-display-name';
import type { StringMap } from 'corgi-types';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { Locale } from '../enums/Locale';
import { Region } from '../enums/Region';
import { BrandTheme } from '../theme/theme';
import type { LocaleMap } from '../types/generic';
import { captureMessage } from '../utils/error-reporter';
import { replaceParams } from '../utils/translation-replace';
import localeOptions from './locale-options.json';

interface LocaleProviderProps {
  /** Component locale */
  locale: string;
  /** Component translation key value pairs */
  locales: StringMap<string>;
  /** React children */
  children: ReactNode;
}

interface L10nProp {
  (str: string, obj?: Record<string, string | number>): string;
  t: (key: string, replaceMap?: Record<string, string | number>) => string;
}

export interface Language {
  language: string;
  region: Region;
  isCurrent: boolean;
  pathPrefix: string;
}

export type Translate = (key: string) => string;

export type Replace = L10nProp;

export interface Prefix {
  (str: string): string;
  t: (key: string) => string;
}

interface LocaleProviderState {
  language: string;
  region: Region;
  locale: Locale;
  locales: LocaleMap<string>;
  theme: BrandTheme;
  r: L10nProp;
  replace: L10nProp;
  t: Translate;
  translate: Translate;
  p: Prefix;
  prefix: Prefix;
  getLanguages: () => Language[];
  l: () => Language[];
  fn: (key: string | number) => string;
  formatNumber: (key: string | number, fractionDigits?: number) => string;
  fc: (key: string | number) => string;
  formatCurrency: (key: string | number) => string;
  currency?: string;
}

interface LocaleOption {
  groupSeparator: string;
  fractionSeparator: string;
  currency: string;
  currencySign: string;
  currencyPosition: string;
}

type UseLocalization = () => LocaleProviderState;

const LOCALES = ['de_AT', 'de_CH', 'de_DE', 'fr_BE', 'fr_CH', 'fr_FR', 'it_IT', 'nl_BE', 'nl_NL'];

const MULTILINGUAL_REGIONS = [Region.Ch, Region.Be];

const options: StringMap<LocaleOption> = localeOptions;
export const LocaleContext = React.createContext<any>(
  process.env.NODE_ENV === 'test'
    ? {
        t: (_: string) => `{{${_}}}`,
        translate: (_: string) => `{{${_}}}`,
        r: (_: any) => _,
        fn: (_: any) => _,
        formatNumber: (_: any) => _,
        fc: (_: any) => _,
        formatCurrency: (_: any) => _,
        replace: (() => {
          const replace = (_ = '') => _;
          replace.t = (_: string) => `{{${_}}}`;

          return replace;
        })(),
        locale: 'de_DE_h24',
        locales: {},
        language: 'de',
        region: Region.De,
        theme: BrandTheme.h24,
        getLanguages: () => [
          {
            isCurrent: true,
            pathPrefix: '',
            language: 'de',
            region: Region.De,
          },
        ],
        l: () => [
          {
            isCurrent: true,
            pathPrefix: '',
            language: 'de',
            region: Region.De,
          },
        ],
        prefix: (() => {
          const prefix = (_ = '') => _;
          prefix.t = (_: string) => `{{${_}}}`;

          return prefix;
        })(),
        p: (() => {
          const prefix = (_ = '') => _;
          prefix.t = (_: string) => `{{${_}}}`;

          return prefix;
        })(),
      }
    : null
);

export class LocaleProvider extends React.Component<LocaleProviderProps, LocaleProviderState> {
  static defaultProps = {
    locales: {},
    locale: Locale.De_De_H24,
  };

  constructor(props: LocaleProviderProps) {
    super(props);

    if (!Object.values(Locale).includes(props.locale as Locale)) {
      throw new Error(`Locale ${props.locale} is not valid.`);
    } else {
      const localeConfig = this.splitLocale(props.locale as Locale);

      const prefix = this.prefix.bind(this);
      const replace = this.replace.bind(this);
      const formatNumber = this.formatNumber.bind(this);
      const formatCurrency = this.formatCurrency.bind(this);

      // Allows to generate prefixed urls easier
      //
      // @example (both will produce the same result)
      //
      //   l10n.prefix(l10n.t('h24_some_url')) -> '/fr/some-url'
      //   l10n.prefix.t('h24_some_url') -> '/fr/some-url'
      Object.defineProperty(prefix, 't', {
        value: (key: string) => this.prefix(this.translate(key)),
      });

      // Allows to generate translations with replaced placeholders
      Object.defineProperty(replace, 't', {
        value: (key: string, replaceMap: Record<string, string>) =>
          this.replace(this.translate(key), replaceMap),
      });

      this.state = {
        locales: {
          [localeConfig.locale]: props.locales,
        },
        ...localeConfig,
        t: this.translate.bind(this),
        translate: this.translate.bind(this),
        r: replace,
        replace,
        l: this.getLanguages.bind(this),
        getLanguages: this.getLanguages.bind(this),
        p: prefix,
        prefix,
        fn: formatNumber,
        formatNumber,
        fc: formatCurrency,
        formatCurrency,
      };
    }
  }

  splitLocale(locale: Locale) {
    const [language, region, theme = BrandTheme.h24] = locale.split('_') as [
      string,
      Region,
      BrandTheme,
    ];

    return { locale, language, theme, region };
  }

  getLanguages() {
    const { language: currentLanguage, region: currentRegion } = this.state;

    return LOCALES.filter(locale => locale.includes(this.state.region)).map(locale => {
      const [language, region] = locale.split('_') as [string, Region];

      const isCurrent = language === currentLanguage && region === currentRegion;

      // INFO: This is base url path prefix dictated by how Shop-Reboot
      // does localization. For example for h24.ch pathPrefix will be
      // "de", for h24.fr -> "fr", for h24.de -> "" as pathPrefix is
      // needed only for multilingual countries
      //
      // Helpful for prefixing urls and API endpoints in some weird
      // cases (hello, Reboot search and cookie policy API 👋 )
      const pathPrefix = MULTILINGUAL_REGIONS.includes(region) ? `/${language}` : '';

      return { language, region, isCurrent, pathPrefix };
    });
  }

  prefix(url = '') {
    const { pathPrefix = '' } = this.getLanguages().find(lang => lang.isCurrent) || {};

    return `${pathPrefix}${url}`;
  }

  translate(key: string) {
    const { locale, locales } = this.state;

    const translation = locales[locale]?.[key];

    if (!translation) {
      const errorMessage = `No translation found for key: ${key}`;

      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.warn(errorMessage);
      } else {
        captureMessage(errorMessage, { kind: 'translation' });
      }

      return `{{${key}}}`;
    } else {
      return translation;
    }
  }

  formatNumber(n: string | number, fractionDigits: number = 2) {
    const num = Number(n);

    if (isNaN(num)) {
      return `${n}`;
    }

    // Placeholder for fraction separator in float values.
    const fractionReplacer = '.';

    // Placeholder for number grouping.
    // Matches the carret before every 3 number
    // eg; 1(match)234(match)567
    const groupReplacer = /\B(?=(\d{3})+(?!\d))/;

    const { fractionSeparator = '', groupSeparator = '' } = options[this.props.locale] || {};

    return num
      .toFixed(fractionDigits)
      .toString()
      .replace(fractionReplacer, fractionSeparator)
      .replace(groupReplacer, groupSeparator);
  }

  formatCurrency(amnt: number | string) {
    const amount = Number(amnt);

    if (isNaN(amount)) {
      return `${amnt}`;
    }

    // Prices are always sent as integer.
    // eg; 1,00 € sent as 100 cents
    const amountWithFraction = amount / 100;
    const formattedAmount = this.formatNumber(amountWithFraction);

    const { currencySign = '', currencyPosition = '' } = options[this.props.locale] || {};

    return this.state.r(currencyPosition, {
      amount: formattedAmount,
      currency: currencySign,
    });
  }

  replace(str: string, obj: Record<string, string>) {
    return replaceParams(str, obj);
  }

  componentDidUpdate(prevProps: Readonly<LocaleProviderProps>) {
    const { locale, locales } = this.props;

    if (prevProps.locale !== locale) {
      this.setState(this.splitLocale(locale as Locale));
    }

    if (prevProps.locales !== locales) {
      this.setState({ locales: { ...locales, [locale]: locales } });
    }
  }

  render() {
    return (
      <LocaleContext.Provider value={this.state}>{this.props.children}</LocaleContext.Provider>
    );
  }
}

interface LocaleConsumerProps {
  children: (l10n: LocaleProviderState) => ReactNode | ReactNode[];
}
export const LocaleConsumer = ({ children }: LocaleConsumerProps) => (
  <LocaleContext.Consumer>
    {l10n => {
      if (l10n === null) {
        throw new TypeError(
          'It seems that you are trying to use LocaleConsumer out of the LocaleProvider scope. ' +
            'Please, check your render tree'
        );
      }

      return children(l10n);
    }}
  </LocaleContext.Consumer>
);

export interface LocaleProps {
  l10n: LocaleProviderState;
}

export function withLocale<T extends object>(Component: ReactComponentType<T>) {
  const WithLocale = (props: Omit<T, 'l10n'> & Partial<LocaleProps>) => (
    <LocaleConsumer>
      {(l10n: LocaleProviderState) => React.createElement(Component, { l10n, ...props } as T)}
    </LocaleConsumer>
  );

  WithLocale.displayName = `withLocale(${name(Component)})`;

  return hoistNonReactStatics(WithLocale, Component as any);
}

export const useLocalization: UseLocalization = () => useContext(LocaleContext);

export const isTranslationAvailable = (content: string) => !/{{(\S+?)}}/.test(content);
